import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter, StaticContext, useParams } from 'react-router';
import Title from '../../components/Title';
import Label from '../../components/Label';
import SmallButton from '../../components/SmallButton';
import { axiosKK } from '../../services/networkRequest';
import { RemovalQuoteRequest } from '../../models/APIModels';
import Moment from 'moment';
import { ArrowLeftCircleIcon } from '@heroicons/react/24/solid';

interface QuoteParams {
  id: string;
}

interface QuoteComponentState {
  quote?: RemovalQuoteRequest;
}

function AdminRemovalQuoteDetailComponent({
  history,
}: RouteComponentProps<undefined, StaticContext, QuoteComponentState>) {
  const { id } = useParams<QuoteParams>();
  const [quote, setQuote] = useState<RemovalQuoteRequest | undefined>(
    history.location?.state?.quote
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!quote) {
      axiosKK
        .get<{ quote: RemovalQuoteRequest }>(`/admin/removals/quotes/${id}`)
        .then(({ data }) => {
          setQuote(data.quote);
        })
        .catch((error) => {
          console.error('Error fetching quote:', error);
        });
    }
  }, [quote, id]);

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#FFA726';
      case 'quoted':
        return '#4CAF50';
      case 'accepted':
        return '#2196F3';
      case 'rejected':
        return '#F44336';
      case 'cancelled':
        return '#9E9E9E';
      default:
        return '#757575';
    }
  };

  if (!quote) {
    return (
      <>
        <Title>Loading...</Title>
        <p>Loading quote request details...</p>
      </>
    );
  }

  return (
    <>
      <section className="nav-status-bar">
        <div className="status-bar-content">
          <ArrowLeftCircleIcon width={20} height={20} />
          <SmallButton onClick={() => history.push('/admin/dashboard')}>
            Back to Dashboard
          </SmallButton>
        </div>
      </section>

      <Title>{`Custom Removal Quote Request #${quote.id}`}</Title>

      <section className="print-doc-and-items-block">
        <div className="print-doc admin-print-doc">
          <div className="top-border">Quote Request Information</div>
          <div className="second-border">
            <p className="roomPack">
              <span
                style={{
                  backgroundColor: getStatusBadgeColor(quote.status),
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                }}
              >
                Status: {quote.status.toUpperCase()}
              </span>
            </p>
            <p className="date">
              Quote Requested At:{' '}
              {Moment(quote.createdAt).format('dddd, MMM Do YYYY')}
            </p>
          </div>
          <div className="basic-block">
            <div className="id-city-requirement">
              <div className="id-and-city">
                <p className="id">#{quote.id}</p>
                <p className="city">{quote.tierName}</p>
                {quote.customerId && (
                  <div
                    onClick={() => history.push(`/admin/customer/${quote.customerId}`)}
                    className="name"
                    style={{ cursor: 'pointer' }}
                  >
                    <p>View Customer Profile</p>
                  </div>
                )}
              </div>
            </div>
            <div className="addresses-block">
              <div className="each">
                <Label>Customer Email:</Label>
                <p>
                  <a href={`mailto:${quote.customerEmail}`}>{quote.customerEmail}</a>
                </p>
              </div>
              <div className="each">
                <Label>Customer Phone:</Label>
                <p>
                  {quote.customerPhone ? (
                    <a href={`tel:${quote.customerPhone}`}>{quote.customerPhone}</a>
                  ) : (
                    'Not provided'
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Message Section */}
          <div className="customer-print-doc">
            <div className="items-block">
              <div className="eachBox">
                <div className="border-top" />
                <div className="content">
                  <div className="basic">
                    <h3>Customer Message</h3>
                  </div>
                  <div className="cost">
                    <div
                      style={{
                        backgroundColor: '#f5f5f5',
                        padding: '15px',
                        borderRadius: '4px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {quote.message || 'No message provided'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

export default withRouter(AdminRemovalQuoteDetailComponent as any);

